'use client';

import { useMemo, useState, useRef, useCallback, useEffect } from 'react';
import CurrencySelector from '@/components/utils/CurrencySelector';
import CalculatorInputs from './CalculatorInputs';
import ResultsDisplay from './ResultsDisplay';
import { calculateCagr } from './calculations';
import { CagrValues } from './types';
import { ToolsTranslations } from '@/lib/translations/types';
import CagrChart from './CagrChart';
import { useToolTracking } from '@/lib/analytics';

interface Props {
  translations: ToolsTranslations['cagr'];
}

export default function CagrCalculator({ translations }: Props) {
  const [values, setValues] = useState<CagrValues>({
    startingAmount: 10000,
    endingAmount: 20000,
    years: 5,
    currency: 'kr',
  });
  const [manualCagr, setManualCagr] = useState<number | null>(null);

  // Initialize tracking
  const { trackUsage, trackCurrencyChange, trackChartInteraction } = useToolTracking('cagr');
  
  // Track if we've already sent a usage event for this session
  const hasTrackedUsage = useRef(false);
  const debounceTimeout = useRef<NodeJS.Timeout | null>(null);

  // Debounced tracking function - only track once per session after user stops interacting
  const debouncedTrackUsage = useCallback(() => {
    // Clear any existing timeout
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }
    
    // Only track if we haven't already tracked usage in this session
    if (!hasTrackedUsage.current) {
      debounceTimeout.current = setTimeout(() => {
        trackUsage();
        hasTrackedUsage.current = true;
      }, 2000); // Wait 2 seconds after last interaction
    }
  }, [trackUsage]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
    };
  }, []);

  const handleValuesChange = (field: keyof CagrValues, value: number | string) => {
    setValues(prev => ({ ...prev, [field]: value }));
    // Reset manual CAGR when inputs change
    setManualCagr(null);
    // Track tool usage with debouncing - only once per session
    debouncedTrackUsage();
  };

  const handleManualCagrChange = (cagr: number) => {
    setManualCagr(cagr);
    // Calculate what ending amount this CAGR would produce
    const newEndingAmount = Math.round(values.startingAmount * Math.pow(1 + cagr, values.years));
    setValues(prev => ({ ...prev, endingAmount: newEndingAmount }));
    // Track chart interaction
    trackChartInteraction();
  };

  const result = useMemo(() => calculateCagr(values, translations), [values, translations]);
  
  // Use manual CAGR if set, otherwise use calculated result
  const displayCagr = manualCagr !== null ? manualCagr : result.cagr;

  return (
    <section id="cagr-calculator" className="w-full my-12 tools">
      <div className="rounded-xl border border-gray-200 shadow-sm bg-white">
        <div className="p-6 border-b border-gray-100">
          <div className="flex items-center justify-between gap-4">
            <div>
              <h2 className="text-xl font-semibold text-blue-900">{translations.title}</h2>
              <p className="text-sm text-gray-600 mt-1">{translations.subtitle}</p>
            </div>
            <div className="w-56">
              <CurrencySelector
                value={values.currency}
                onChange={(currency) => {
                  handleValuesChange('currency', currency);
                  trackCurrencyChange();
                }}
                showArrow={false}
                localStorageKey="currencyPreference"
              />
            </div>
          </div>
        </div>
        <div className="p-6 space-y-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <CalculatorInputs values={values} onValuesChange={handleValuesChange} translations={translations} />
            <ResultsDisplay
              result={result}
              translations={translations}
              startingAmount={values.startingAmount}
              endingAmount={values.endingAmount}
              years={values.years}
              currency={values.currency}
            />
          </div>
          <CagrChart
            startingAmount={values.startingAmount}
            cagr={displayCagr}
            years={values.years}
            translations={translations}
            onCagrChange={handleManualCagrChange}
            currency={values.currency}
          />
        </div>
      </div>
    </section>
  );
}


